import AppStoreBadge from "@/assets/images/app-store-badge.svg";
import { Ionicons } from "@expo/vector-icons";
import { useViewportSlideFadeIntro } from "@/hooks/useViewportSlideFadeIntro";
import { APP_STORE_URL } from "@/lib/appStore";
import React from "react";
import { Animated, Linking, Platform, Pressable, StyleSheet, View } from "react-native";
import Svg, { Path } from "react-native-svg";
import { XStack, YStack, useMedia } from "tamagui";
import { BodyText } from "@/components/ui/Typography";

const PAGE_BG = "#F4F1ED";
const FOOTER_BG = "#000000";
const FOOTER_TEXT = "#FFFFFF";

const FOOTER_COLUMNS: { label: string; href: string }[][] = [
  [
    {
      label: "About Open Studio",
      href: "https://www.openstudiojazz.com/about-us/",
    },
    { label: "Contact Us", href: "https://www.openstudiojazz.com/contact/" },
    {
      label: "Reviews",
      href: "https://www.reviews.io/company-reviews/store/open-studio-jazz",
    },
  ],
  [
    {
      label: "You'll Hear It Podcast",
      href: "https://youllhearit.com/",
    },
    {
      label: "Satisfaction Guarantee",
      href: "https://www.openstudiojazz.com/guarantee/",
    },
    {
      label: "Terms of Service",
      href: "https://www.openstudiojazz.com/terms-of-service/",
    },
    {
      label: "Privacy Policy",
      href: "https://www.openstudiojazz.com/privacy-policy/",
    },
  ],
];

const SOCIAL_LINKS: {
  icon: keyof typeof Ionicons.glyphMap;
  href: string;
  label: string;
}[] = [
  {
    icon: "mail-outline",
    href: "mailto:support@openstudiojazz.com",
    label: "Email",
  },
  {
    icon: "logo-youtube",
    href: "https://www.youtube.com/openstudiojazz",
    label: "YouTube",
  },
  {
    icon: "logo-instagram",
    href: "https://www.instagram.com/openstudiojazz/",
    label: "Instagram",
  },
  {
    icon: "logo-tiktok",
    href: "https://www.tiktok.com/@openstudiojazz",
    label: "TikTok",
  },
  {
    icon: "logo-facebook",
    href: "https://www.facebook.com/openstudiojazz",
    label: "Facebook",
  },
];

// Apple's official "Download on the App Store" badge (intrinsic 119.66×40);
// Apple's guidelines ask for a minimum height of 40px and clear space around it.
const APP_STORE_BADGE_HEIGHT = 40;
const APP_STORE_BADGE_WIDTH = Math.round(APP_STORE_BADGE_HEIGHT * (119.66407 / 40));

const WAVE_PATH =
  "M874 60C752 50 396 0 0 0V342H1439V24C1294 111.904 965.693 67.5158 874 60Z";

const FOOTER_BAND_HEIGHT_DESKTOP_PX = 342;
const FOOTER_BAND_HEIGHT_MOBILE_PX = 430;
const FOOTER_LINK_CHEVRON_TEXT_GAP_PX = 20;

/** Same orange as header nav icons (ResponsiveHeader, GlobalSearchManager, ExploreMenu). */
const FOOTER_LINK_HOVER = "rgba(242, 102, 58, 1)";

function FooterLinkChevron({ stroke }: { stroke: string }) {
  return (
    <Svg
      width={6}
      height={12}
      viewBox="0 0 6 12"
      style={{ flexShrink: 0 }}
    >
      <Path
        d="M1 1 L5 6 L1 11"
        fill="none"
        stroke={stroke}
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function FooterLink({ label, href }: { label: string; href: string }) {
  const [hovered, setHovered] = React.useState(false);
  const accent = hovered ? FOOTER_LINK_HOVER : FOOTER_TEXT;

  return (
    <Pressable
      onPress={() => Linking.openURL(href)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="link"
      accessibilityLabel={label}
      style={{ cursor: "pointer", alignSelf: "flex-start" } as object}
    >
      <XStack ai="center" gap={FOOTER_LINK_CHEVRON_TEXT_GAP_PX}>
        <FooterLinkChevron stroke={accent} />
        <BodyText
          color={accent}
          style={
            {
              leadingTrim: "both",
              textEdge: "cap",
            } as object
          }
        >
          {label}
        </BodyText>
      </XStack>
    </Pressable>
  );
}

function SocialButton({
  icon,
  href,
  label,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  href: string;
  label: string;
}) {
  const [hovered, setHovered] = React.useState(false);
  const iconColor = hovered ? FOOTER_LINK_HOVER : "#0D0D0D";

  return (
    <Pressable
      onPress={() => Linking.openURL(href)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="link"
      accessibilityLabel={label}
      style={{ cursor: "pointer" } as object}
    >
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: 16,
          backgroundColor: "#fff",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Ionicons name={icon} size={18} color={iconColor} />
      </View>
    </Pressable>
  );
}

function AppStoreBadgeLink() {
  const [hovered, setHovered] = React.useState(false);

  return (
    <Pressable
      onPress={() => Linking.openURL(APP_STORE_URL)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="link"
      accessibilityLabel="Download the Open Studio app on the App Store"
      style={{ cursor: "pointer", opacity: hovered ? 0.85 : 1 } as object}
    >
      <AppStoreBadge width={APP_STORE_BADGE_WIDTH} height={APP_STORE_BADGE_HEIGHT} />
    </Pressable>
  );
}

export function CoursePageFooter() {
  if (Platform.OS !== "web") return null;

  const media = useMedia();
  const rowLinks = media.gtSm;
  const footerBandHeightPx = rowLinks
    ? FOOTER_BAND_HEIGHT_DESKTOP_PX
    : FOOTER_BAND_HEIGHT_MOBILE_PX;
  const overlayPaddingBottomPx = rowLinks ? 40 : 56;
  const overlayPaddingTopPx = rowLinks ? 16 : 12;
  const socialIconsGapPx = rowLinks ? 16 : 20;

  const { shellRef, translateY, overlayOpacity } = useViewportSlideFadeIntro({
    slideDistancePx: footerBandHeightPx,
  });

  return (
    <YStack w="100%" mt="$6" als="stretch">
      <View
        ref={shellRef}
        collapsable={false}
        style={{
          width: "100%",
          height: footerBandHeightPx,
          position: "relative",
          backgroundColor: PAGE_BG,
          overflow: "hidden",
        }}
      >
        <Animated.View
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            height: footerBandHeightPx,
            transform: [{ translateY }],
          }}
        >
          <View style={[StyleSheet.absoluteFillObject, { backgroundColor: "transparent" }]}>
            <Svg
              width="100%"
              height="100%"
              viewBox="0 0 1439 342"
              preserveAspectRatio="none"
            >
              <Path d={WAVE_PATH} fill={FOOTER_BG} />
            </Svg>
          </View>
        </Animated.View>
        <Animated.View
          style={[
            StyleSheet.absoluteFillObject,
            {
              justifyContent: "flex-end",
              alignItems: "center",
              paddingBottom: overlayPaddingBottomPx,
              paddingTop: overlayPaddingTopPx,
              pointerEvents: "box-none",
              opacity: overlayOpacity,
            },
          ]}
        >
          <YStack
            w="100%"
            maw={1140}
            als="center"
            px={16}
            $gtSm={{ px: 32 }}
            gap="$4"
            pointerEvents="auto"
          >
            <XStack
              flexWrap="wrap"
              gap="$5"
              jc="flex-start"
              ai="flex-start"
              $gtSm={{ gap: "$9" }}
            >
              {FOOTER_COLUMNS.map((col, i) => (
                <YStack
                  key={i}
                  gap="$2"
                  minWidth={rowLinks ? 200 : "100%"}
                  flex={rowLinks ? undefined : 1}
                  als="stretch"
                >
                  {col.map((item) => (
                    <FooterLink key={item.href} {...item} />
                  ))}
                </YStack>
              ))}
            </XStack>

            <XStack
              flexWrap="wrap"
              jc="space-between"
              ai="center"
              w="100%"
              gap="$3"
              marginTop={rowLinks ? -12 : -8}
            >
              <AppStoreBadgeLink />
              <XStack flexWrap="wrap" gap={socialIconsGapPx} jc="flex-end" ai="center">
                {SOCIAL_LINKS.map((s) => (
                  <SocialButton key={s.label} {...s} />
                ))}
              </XStack>
            </XStack>
          </YStack>
        </Animated.View>
      </View>
    </YStack>
  );
}
